import store from 'store';
import modulesMap from 'core/modulesMap';
import MainPage from 'navigation/pages/MainPage';
import scroll from 'core/scroll';
import { bindEmitterMethod, bindMethod } from 'helpers/bind';
import { defer } from 'lodash-es';
import mqStore from 'store/mqStore';
import AddToWishlist from 'components/add-to-wishlist/AddToWishlist';
import DragController from 'components/drag-controller/DragController';
import anime from 'animejs';
import { frameToMs } from 'helpers/easings';
import { forceArray } from 'navigation/component/Component';
import ProductGallery from './partials/ProductGallery';
import ProductBar from './partials/ProductBar';
import ProductZoom from './partials/ProductZoom';
import ProductShopLook from './partials/ProductShopLook';
import ProductColorSelector from './partials/ProductColorSelector';
import ProductCard from './partials/ProductCard';
import ProductBreadcrumbs from './ProductBreadcrumbs';
/**
 * Common behaviour shared by every product page (simple or bundle):
 * global modules, gallery/color-selector wiring and the sticky add-to-cart
 * bar that reacts to scroll. Product-type-specific logic lives in
 * SimpleProduct and BundleProduct.
 */
class Product extends MainPage {
    productDetailsBottom = 0;
    productGalleryBottom = 0;
    mobileHeaderHeight = 56;
    getModulesMap() {
        const map = Object.assign({}, modulesMap);
        delete map.form;
        return {
            ...map,
            bar: ['.product-bar', ProductBar],
            zoom: ['.product-zoom', ProductZoom],
            gallery: ['.product-gallery', ProductGallery],
            shopLook: ['.shop-look', ProductShopLook],
            colorSelector: ['.color-selector', ProductColorSelector],
            addToWishlistLarge: ['.add-to-wishlist__large', AddToWishlist],
            xlinking: ['.xlinking__list', DragController],
            labels: ['.product-labels__small', DragController],
            breadcrumbs: ['.product__breadcrumbs', ProductBreadcrumbs],
            card: ['.product-card__related', ProductCard]
        };
    }
    async preload(previousPage) {
        await this.preloadImages();
    }
    getModuleParams(el, componentConstructor) {
        return {
            ...super.getModuleParams(el, componentConstructor),
            gradient: true
        };
    }
    initialized() {
        super.initialized();
        defer(() => {
            this.productDetailsBottom = this.modules.gallery.el.getBoundingClientRect().bottom - this.refs.productDetailsInner.getBoundingClientRect().bottom;
        });
    }
    async show(previousPage) {
        await super.show(previousPage);
        window.dispatchEvent(new CustomEvent('product_view'));
        return Promise.resolve();
    }
    bindEvents(add) {
        const method = bindMethod(add);
        const emitterMethod = bindEmitterMethod(add);
        // toggles with a data-panel open a dynamic panel through the internal router instead of the drawer
        forceArray(this.refs.toggleColorSelector).forEach(toggle => toggle?.dataset.panel === undefined && toggle?.[method]('click', this.onToggleColorSelector));
        forceArray(this.refs.infosToggle).forEach(toggle => toggle?.[method]('click', this.onInfosClick));
        forceArray(this.refs.labelLinks).forEach(link => link?.[method]('click', this.onLabelLinkClick));
        scroll[emitterMethod](this.onScroll);
    }
    // smooth scroll scoped to label anchors: html scroll-behavior would also animate scroll locks/resets
    onLabelLinkClick = (e) => {
        const target = document.getElementById(e.currentTarget.hash.slice(1));
        if (!target)
            return;
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth' });
    };
    onInfosClick = (e) => {
        store.infosAccordion.set(e.currentTarget.dataset.open);
        store.drawer.set('infos');
    };
    onToggleColorSelector = () => {
        store.drawer.set('color-selector');
    };
    async hide(nextPage) {
        this.el.classList.add('disappearing');
        if (nextPage === 'Category' && !!store.lastProduct.get()) {
            return anime({
                targets: this.el,
                opacity: {
                    value: [1, 0],
                    easing: 'linear',
                    duration: 0
                }
            }).finished.then(() => {
            });
        }
        return anime({
            targets: this.el,
            opacity: {
                value: [1, 0],
                easing: 'linear',
                duration: frameToMs(7.5)
            }
        }).finished.then(() => {
        });
    }
    onScroll = () => {
        if (this._hiding)
            return;
        const isTabletPortrait = mqStore.tabletPortrait.get();
        let belowBottom = false;
        const galleryAbove = scroll.scrollTop() > this.productGalleryBottom;
        if (isTabletPortrait)
            belowBottom = this.productDetailsBottom - 100 > 0 && (scroll.scrollTop() + window.innerHeight - 180) > this.productDetailsBottom;
        else
            belowBottom = this.productDetailsBottom - 100 > 0 && scroll.scrollTop() > this.productDetailsBottom;
        if (!isTabletPortrait)
            this.modules.bar.visible = belowBottom;
        else
            this.modules.bar.el.classList.toggle('in-flow', belowBottom);
        if (this.modules.bar.visible && (belowBottom || galleryAbove))
            store.headerVariants.set(['solid', 'light']);
        else
            store.headerVariants.set(['transparent', 'light']);
    };
    /**
     * Height of the sticky details panel to account for once it stops
     * being sticky (tablet portrait). SimpleProduct adds its infos block;
     * BundleProduct only has the details panel itself.
     */
    getTabletPortraitDetailsHeight() {
        return this.refs.productDetailsInner.offsetHeight;
    }
    resize() {
        super.resize();
        this.productGalleryBottom = this.modules.gallery.el.offsetHeight - this.mobileHeaderHeight;
        if (mqStore.tabletPortrait.get())
            this.productDetailsBottom = this.productGalleryBottom + this.getTabletPortraitDetailsHeight();
        else
            this.productDetailsBottom = (this.modules.gallery.el.offsetTop + this.modules.gallery.el.offsetHeight) - (this.refs.productDetailsInner.offsetTop + this.refs.productDetailsInner.offsetHeight);
    }
}
export default Product;
